<script setup lang="ts">
import { computed } from "vue";
import { useHead } from "@unhead/vue";

import { pages } from "@/content/pages";
import ContactForm from "@/components/ContactForm.vue";
import type { SitePage } from "@/types";

const props = defineProps<{ page: SitePage }>();
const utilityPaths = new Set([
  "/support/",
  "/accessibility/",
  "/privacy-policy/",
  "/terms-of-service/",
  "/refund-policy/",
  "/delete-account/",
  "/sitemap/",
]);
const isHome = computed(() => props.page.path === "/");
const isUtility = computed(() => utilityPaths.has(props.page.path));
const canonical = computed(() => `https://mysimplebible.com${props.page.path}`);
const heroImage = computed(() => props.page.heroImage ?? "/media/ios-reading.webp");
const heroImageAlt = computed(
  () => props.page.heroImageAlt ?? "MySimpleBible reading screen on iPhone",
);
const iosCompanions: Record<string, string> = {
  "/media/android-reading.webp": "/media/ios-reading.webp",
  "/media/android-notes.webp": "/media/ios-notes.webp",
  "/media/android-pray-now.webp": "/media/ios-pray-now.webp",
  "/media/android-prayers.webp": "/media/ios-prayer-calendar.webp",
};
const androidCompanions: Record<string, string> = {
  "/media/ios-reading.webp": "/media/android-reading.webp",
  "/media/ios-notes.webp": "/media/android-notes.webp",
  "/media/ios-pray-now.webp": "/media/android-pray-now.webp",
  "/media/ios-prayer-calendar.webp": "/media/android-prayers.webp",
};
const ANDROID_SHOT = { width: 1078, height: 2588 } as const;

const IOS_SHOTS: Record<string, { width: number; height: number }> = {
  "/media/ios-reading.webp": { width: 1206, height: 2799 },
  "/media/ios-pray-now.webp": { width: 1206, height: 2799 },
  "/media/ios-prayer-calendar.webp": { width: 1206, height: 2799 },
  "/media/ios-notes.webp": { width: 1206, height: 2622 },
  "/media/ios-settings.webp": { width: 1206, height: 2622 },
};
const IOS_FALLBACK = { width: 1206, height: 2622 } as const;
const iosShot = (src: string) => IOS_SHOTS[src] ?? IOS_FALLBACK;

const platformPair = (image: string) => ({
  ios: image.includes("/ios-")
    ? image
    : (iosCompanions[image] ?? "/media/ios-reading.webp"),
  android: image.includes("/android-")
    ? image
    : (androidCompanions[image] ?? "/media/android-reading.webp"),
});
const heroPair = computed(() => platformPair(heroImage.value));
const heroIosAlt = computed(() =>
  heroImage.value.includes("/ios-")
    ? heroImageAlt.value
    : "MySimpleBible running on iPhone",
);
const heroAndroidAlt = computed(() =>
  heroImage.value.includes("/android-")
    ? heroImageAlt.value
    : "MySimpleBible running on Android",
);
const external = (href: string) => /^https?:\/\//u.test(href);

useHead(() => ({
  title: props.page.title,
  link: [{ rel: "canonical", href: canonical.value }],
  meta: [
    { name: "viewport", content: "width=device-width, initial-scale=1, viewport-fit=cover" },
    { name: "description", content: props.page.description },
    { property: "og:title", content: props.page.title },
    { property: "og:description", content: props.page.description },
    { property: "og:url", content: canonical.value },
    { property: "og:image", content: "https://mysimplebible.com/media/social-card.png" },
  ],
}));

const sitemapTarget = (label: string) => pages.find((page) => page.label === label)?.path;
</script>

<template>
  <article :class="['product-page', { 'product-page--utility': isUtility, 'product-page--home': isHome }]">
    <section class="hero-band">
      <div class="hero-copy">
        <p class="eyebrow">{{ page.eyebrow }}</p>
        <h1>{{ page.heading }} <span>{{ page.accent }}</span></h1>
        <p class="hero-lede">{{ page.introduction }}</p>
        <div class="button-row">
          <a v-if="page.path === '/support/'" href="#contact" class="button button--primary">Contact Support <span aria-hidden="true">↘</span></a>
          <RouterLink v-else :to="page.cta.to" class="button button--primary">{{ page.cta.label }} <span aria-hidden="true">↗</span></RouterLink>
          <RouterLink v-if="!isUtility && page.path !== '/features/' && page.cta.to !== '/features/'" to="/features/" class="button button--secondary">Explore Features <span aria-hidden="true">↘</span></RouterLink>
        </div>
      </div>
      <div v-if="!isUtility" class="hero-gallery" aria-label="The app on iPhone and Android">
        <div class="hero-devices">
          <figure class="device-bay"><figcaption>iPhone <span>iOS</span></figcaption><div class="phone phone--ios"><img :src="heroPair.ios" :alt="heroIosAlt" :width="iosShot(heroPair.ios).width" :height="iosShot(heroPair.ios).height" fetchpriority="high" /></div></figure>
          <figure class="device-bay"><figcaption>Android <span>Native App</span></figcaption><div class="phone phone--android"><img :src="heroPair.android" :alt="heroAndroidAlt" :width="ANDROID_SHOT.width" :height="ANDROID_SHOT.height" /></div></figure>
        </div>
      </div>
    </section>
    <ul v-if="!isUtility" class="hero-facts"><li><strong>Read</strong><span>The Bible, offline</span></li><li><strong>Listen</strong><span>Narration on your device</span></li><li><strong>Remember</strong><span>Notes &amp; highlights</span></li><li><strong>Pray</strong><span>A place for every request</span></li></ul>

    <ContactForm v-if="page.path === '/support/'" app="MySimpleBible" />

    <nav v-if="!isHome && page.sections.length > 1" class="page-directory" aria-label="On This Page">
      <p class="eyebrow">On This Page</p><div><a v-for="(section, index) in page.sections" :key="section.title" :href="`#section-${index + 1}`">{{ section.label ?? section.title }}</a></div>
    </nav>

    <section v-if="page.plans" id="plans" class="plans-band">
      <header class="section-intro">
        <p class="section-label"><span>Pro</span>Two Ways To Subscribe</p>
        <h2>One Complete App. Your Choice Of Plan.</h2>
        <p>Choose the billing option that works for you. Both plans include the same MySimpleBible Pro features.</p>
      </header>
      <div class="plan-grid">
        <article v-for="plan in page.plans" :key="plan.name" class="plan-card" :data-featured="Boolean(plan.featured)">
          <div class="plan-summary"><p class="plan-name">{{ plan.name }}</p><p class="plan-price">{{ plan.price }}</p><p class="plan-cadence">{{ plan.cadence }}</p><p class="plan-copy">{{ plan.body }}</p></div>
          <div class="plan-detail"><ul><li v-for="feature in plan.features" :key="feature"><span aria-hidden="true">✓</span>{{ feature }}</li></ul>
            <a v-if="external(plan.href)" :href="plan.href" class="button button--plan" target="_blank" rel="noopener noreferrer">Choose {{ plan.name }}</a>
            <RouterLink v-else :to="plan.href" class="button button--plan">Explore Pro <span aria-hidden="true">↗</span></RouterLink>
          </div>
        </article>
      </div>
    </section>

    <div id="page-sections" class="page-sections">
      <section v-for="(section, index) in page.sections" :id="`section-${index + 1}`" :key="section.title" class="story-band" :data-dark="Boolean(section.dark)" :data-tone="index % 2">
        <header class="section-intro">
          <p class="section-label">{{ section.label ?? page.label }}</p>
          <h2>{{ section.title }}</h2><p>{{ section.body }}</p>
        </header>
        <div v-if="section.panels" class="panel-grid" :data-count="section.panels.length">
          <article v-for="panel in section.panels" :key="panel.title"><h3>{{ panel.title }}</h3><p>{{ panel.body }}</p></article>
        </div>
        <div v-if="section.image || section.points" class="section-detail">
          <ul v-if="section.points" class="point-grid"><li v-for="point in section.points" :key="point"><template v-if="page.path === '/sitemap/' && sitemapTarget(point)"><RouterLink :to="sitemapTarget(point)!">{{ point }} <span aria-hidden="true">↗</span></RouterLink></template><template v-else><span>{{ point }}</span></template></li></ul>
          <figure v-if="section.image" class="product-shot">
            <div class="proof-device-grid">
              <div class="proof-device"><span class="proof-label">iPhone · iOS</span><div class="phone phone--ios"><img :src="platformPair(section.image).ios" :alt="`${section.imageAlt ?? section.title} on iPhone`" :width="iosShot(platformPair(section.image).ios).width" :height="iosShot(platformPair(section.image).ios).height" loading="lazy" decoding="async" /></div></div>
              <div class="proof-device"><span class="proof-label">Android</span><div class="phone phone--android"><img :src="platformPair(section.image).android" :alt="`${section.imageAlt ?? section.title} on Android`" :width="ANDROID_SHOT.width" :height="ANDROID_SHOT.height" loading="lazy" decoding="async" /></div></div>
            </div>
          </figure>
        </div>

      </section>
    </div>
    <section class="closing-band">
      <p class="eyebrow">Keep Scripture Close</p><div><h2>{{ page.cta.title }}</h2><p>{{ page.cta.body }}</p></div>
      <RouterLink :to="page.cta.to" class="button button--light">{{ page.cta.label }} <span aria-hidden="true">↗</span></RouterLink>
    </section>
  </article>
</template>
